import * as React from "react";
import { HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const STORAGE_KEY = "help-guide-seen";

export function useFirstTimeHelp() {
  const [hasSeen, setHasSeen] = React.useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(STORAGE_KEY) === "1";
  });

  const markSeen = React.useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "1");
    setHasSeen(true);
  }, []);

  return { hasSeen, markSeen };
}

export function HelpGuideDialog({ open, onOpenChange }: { open?: boolean; onOpenChange?: (v: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Open help guides">
          <HelpCircle className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Time Tracking Help</DialogTitle>
          <DialogDescription>
            Guides and a video walkthrough to help you get the most out of the timeclock system.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="quick" className="mt-2">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="quick">Quick Guide</TabsTrigger>
            <TabsTrigger value="accuracy">Accuracy Guide</TabsTrigger>
            <TabsTrigger value="video">Video Tutorial</TabsTrigger>
          </TabsList>

          <TabsContent value="quick" className="mt-4">
            <div className="rounded-lg border overflow-hidden">
              <img
                src="/help/Time_Tracking_Quick_Guide.png"
                alt="Time Tracking Quick Guide"
                className="w-full h-auto object-contain"
                loading="lazy"
              />
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              A quick-reference overview of how to clock in, clock out, and submit field notes.
            </p>
          </TabsContent>

          <TabsContent value="accuracy" className="mt-4">
            <div className="rounded-lg border overflow-hidden">
              <img
                src="/help/Accuracy_in_Every_Punch_Guide.png"
                alt="Accuracy in Every Punch Guide"
                className="w-full h-auto object-contain"
                loading="lazy"
              />
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Best practices for ensuring accurate time punches and GPS verification.
            </p>
          </TabsContent>

          <TabsContent value="video" className="mt-4">
            <div className="rounded-lg border overflow-hidden">
              <video
                controls
                preload="metadata"
                className="w-full h-auto"
                poster="/help/Time_Tracking_Quick_Guide.png"
              >
                <source src="/help/Time_Tracker_Help.mp4" type="video/mp4" />
                <track kind="captions" srcLang="en" label="English captions" />
                Your browser does not support the video tag.
              </video>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              A step-by-step video walkthrough of the entire time tracking workflow.
            </p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
